import { format, isToday, isTomorrow } from 'date-fns';
import { LuCheck, LuEllipsis, LuMail, LuPencilLine, LuSparkles } from 'react-icons/lu';
import { MdOutlineCalendarMonth } from 'react-icons/md';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { convertBackendTimeToBrowser } from '@/utils';
import { useAiChatSidebarState } from 'views/ai-chat/ai-chat-state';
import { useAiChatNewConversation } from 'views/ai-chat/useAiChatNewConversation';
import { useDockState } from 'views/dock/dock-state';
import { HouseholdLink } from 'widgets/NameLink';

import { createFollowupPrompt, createPrepPrompt, createTaskPrompt } from './priorities-prompt';
import { Priority, PriorityType } from './priority-types';
import { shouldShowCreateTaskAction } from './priority-utils';

interface PriorityItemProps {
    priority: Priority;
    onComplete?: () => void;
    isCompleting?: boolean;
}

export const PriorityItem = ({ priority, onComplete, isCompleting = false }: PriorityItemProps) => {
    const { open: openChat } = useAiChatSidebarState();
    const { startNewConversation } = useAiChatNewConversation();
    const { addCreateTaskDockItem } = useDockState();

    const handlePrimaryAction = () => {
        if (priority.type === PriorityType.EVENT) {
            openChat('sidekick');
            startNewConversation(createPrepPrompt(priority.data));
            return;
        }
        if (priority.type === PriorityType.TASK) {
            openChat('sidekick');
            startNewConversation(createTaskPrompt(priority.data));
            return;
        }
        if (priority.type === PriorityType.FOLLOWUP) {
            openChat('sidekick');
            startNewConversation(createFollowupPrompt(priority.data));
        }
    };

    const getPrimaryActionLabel = () => {
        if (priority.type === PriorityType.EVENT) return 'Prep';
        if (priority.type === PriorityType.TASK) return 'Brainstorm';
        return 'Draft';
    };

    const handleCreateTask = () => {
        if (!shouldShowCreateTaskAction(priority)) return;
        addCreateTaskDockItem(priority.data.clientHouseholdGUID, undefined, priority.data.eventGUID);
    };

    return (
        <div className="border-border hover:bg-cocoa-0/50 flex items-center gap-6 border-t px-1.5 py-4 transition-colors last:border-b">
            <PriorityIcon type={priority.type} />

            <div className="min-w-0 flex-1">
                <h4 className="text-cocoa-1000 text-base font-thin">{getTitle(priority)}</h4>
                <PrioritySubtitle priority={priority} />
            </div>

            <div className="flex shrink-0 items-center gap-2 pr-2">
                <Button variant="outline2" size="sm" onClick={handlePrimaryAction} className="gap-1.5 px-4 text-base">
                    <LuSparkles className="h-4 w-4" />
                    {getPrimaryActionLabel()}
                </Button>
                <Button
                    variant="outline2"
                    size="icon"
                    onClick={onComplete}
                    className="h-10 w-10"
                    disabled={isCompleting}
                >
                    <LuCheck className="h-4 w-4" />
                </Button>
                {shouldShowCreateTaskAction(priority) && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost2" size="icon" className="h-10 w-10">
                                <LuEllipsis className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleCreateTask}>
                                <LuPencilLine className="mr-2 h-4 w-4" />
                                Create Task
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        </div>
    );
};

const PrioritySubtitle = ({ priority }: { priority: Priority }) => {
    if (priority.type === PriorityType.EVENT) {
        const startTime = convertBackendTimeToBrowser(priority.data.startTime, priority.data.startTimeZone);
        let datePrefix = '';
        if (isToday(startTime)) {
            datePrefix = 'Today';
        } else if (isTomorrow(startTime)) {
            datePrefix = 'Tomorrow';
        } else {
            datePrefix = format(startTime, 'MMM d');
        }
        const timeStr = format(startTime, 'h:mma');
        return (
            <p className="text-cocoa-500 text-sm font-thin">
                <HouseholdLink householdGUID={priority.data.clientHouseholdGUID} /> - {datePrefix} @ {timeStr}
            </p>
        );
    }

    if (priority.type === PriorityType.TASK) {
        if (priority.data.overdue) {
            return (
                <p className="text-cocoa-500 text-sm font-thin">
                    {priority.data.householdGUID && <HouseholdLink householdGUID={priority.data.householdGUID} />}{' '}
                    <span className="text-red-500">- Overdue</span>
                </p>
            );
        }
        return (
            <p className="text-cocoa-500 text-sm font-thin">
                {priority.data.householdGUID && <HouseholdLink householdGUID={priority.data.householdGUID} />}
            </p>
        );
    }

    return (
        <p className="text-cocoa-500 text-sm font-thin">
            <HouseholdLink householdGUID={priority.data.clientHouseholdGUID} />
        </p>
    );
};

const PriorityIcon = ({ type }: { type: PriorityType }) => {
    const icon =
        type === PriorityType.EVENT ? (
            <MdOutlineCalendarMonth className="text-cocoa-700 h-6 w-6" />
        ) : type === PriorityType.TASK ? (
            <LuPencilLine className="text-cocoa-700 h-6 w-6" />
        ) : (
            <LuMail className="text-cocoa-700 h-6 w-6" />
        );
    return (
        <div className="bg-cocoa-0 flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] p-2">{icon}</div>
    );
};

const getTitle = (priority: Priority) => {
    if (priority.type === PriorityType.EVENT) return `${priority.data.title || 'Meeting'} - Prep`;
    if (priority.type === PriorityType.TASK) return priority.data.title || 'Task';
    return `${priority.data.title || 'Meeting'} - Summary`;
};
